/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick, logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

const messages = defineMessages({
    title: { id: 'languageStudio.nextStepsResponsibleAiScreen.title', defaultMessage: 'Responsible use of AI' },
    microsoftAiLink: { id: 'languageStudio.nextStepsResponsibleAiScreen.microsoftAiLink', defaultMessage: `Microsoft’s AI principles` },
    responsibleAiLink: {
        id: 'languageStudio.nextStepsResponsibleAiScreen.responsibleAiLink',
        defaultMessage: 'Responsible use of AI with Cognitive Services'
    },
    description: {
        id: 'languageStudio.nextStepsResponsibleAiScreen.description',
        defaultMessage: `We are committed to the advancement of AI driven by ethical principles that put people first.
        We offer guidance for responsible use of these capabilities based on Microsoft AI’s principles of fairness,
        reliability and safety, privacy and security, inclusiveness, transparency, and human accountability.`
    }
});

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '14px' } });

export const NextStepsResponsibleAiScreen = ({
    isResultsEmpty,
    demoTitle
}: {
    demoTitle: TelemetryFeatureTitle;
    isResultsEmpty: boolean;
}) => {
    const { localize } = useLocalization();

    const handleClickLink = (linkName: string) => {
        logDemoButtonClick(demoTitle, 'Nextsteps:responsibleAi', linkName);
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <Image role="presentation" src={getAssetsUrl('assets/images/languageStudio/prebuiltDemos/responsibleAi.svg')} />

                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.title)}
                </LuisText>
                <LuisText>{localize(messages.description)}</LuisText>
            </Stack>
            <Stack tokens={{ childrenGap: 8 }}>
                <ExternalLink href="https://aka.ms/cogsservices-rai" onClick={() => handleClickLink('ResponsibleAiLink')}>
                    <Stack horizontal verticalAlign="baseline">
                        {localize(messages.responsibleAiLink)}&nbsp;
                        <Icon iconName="MiniExpand" styles={iconStyles} />
                    </Stack>
                </ExternalLink>
                <ExternalLink href="https://aka.ms/microsoft-rai" onClick={() => handleClickLink('MicrosoftAiLink')}>
                    <Stack horizontal verticalAlign="baseline">
                        {localize(messages.microsoftAiLink)}&nbsp;
                        <Icon iconName="MiniExpand" styles={iconStyles} />
                    </Stack>
                </ExternalLink>
            </Stack>
        </Stack>
    );
};
